import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  CreditCard, 
  Send, 
  CheckCircle, 
  Clock, 
  ExternalLink,
  AlertCircle,
  ChevronRight,
  RefreshCw
} from "lucide-react";

const PaymentTracker = ({ proposal, onUpdate }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingPayment, setSendingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [stripeConnected, setStripeConnected] = useState(false);

  const proposalId = proposal?.proposal_id;
  const isAccepted = proposal?.status === "accepted";
  
  // Get pricing section
  const pricingSection = proposal?.sections?.find(s => s.type === "pricing")?.content;
  const hasPricing = !!pricingSection;
  const paymentType = pricingSection?.payment_type || "one_time";
  const splitPayments = pricingSection?.split_payments || [];

  useEffect(() => {
    if (proposalId && isAccepted && hasPricing) {
      checkStripeConnection();
      fetchPaymentStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId, isAccepted]);

  const checkStripeConnection = async () => {
    try {
      const response = await axios.get(`${API}/payments/check-connection`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeConnected(response.data.connected);
    } catch (error) {
      console.error("Error checking Stripe connection:", error);
    }
  };

  const fetchPaymentStatus = async (showRefreshState = false) => {
    if (showRefreshState) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API}/payments/proposals/${proposalId}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentStatus(response.data);
    } catch (error) {
      console.error("Error fetching payment status:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const sendPaymentRequest = async (paymentIndex = null) => {
    setSendingPayment(true);
    try {
      const response = await axios.post(
        `${API}/payments/proposals/${proposalId}/send`,
        { payment_index: paymentIndex },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Payment request sent successfully!");
      fetchPaymentStatus();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send payment request");
    } finally {
      setSendingPayment(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  // Don't show if not accepted or no pricing
  if (!isAccepted || !hasPricing) return null;

  const currentPaymentIndex = paymentStatus?.current_payment_index || 0;
  const payments = paymentStatus?.payments || [];
  const overallStatus = paymentStatus?.status || "not_sent";

  // Calculate totals for display
  const calculateTotal = () => {
    if (paymentType === "one_time") {
      return (pricingSection.items || []).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    }
    if (paymentType === "split") {
      return splitPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    }
    if (paymentType === "subscription") {
      return parseFloat(pricingSection.subscription?.amount) || 0;
    }
    if (paymentType === "hybrid") {
      return (parseFloat(pricingSection.onboarding_fee) || 0) + 
             (parseFloat(pricingSection.subscription?.amount) || 0);
    }
    return 0;
  };

  return (
    <Card className="border-stone">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Payment
            {paymentType === "split" && (
              <Badge variant="outline" className="ml-2 text-xs">Split</Badge>
            )}
            {paymentType === "subscription" && (
              <Badge variant="outline" className="ml-2 text-xs">Subscription</Badge>
            )}
            {paymentType === "hybrid" && (
              <Badge variant="outline" className="ml-2 text-xs">Setup + Subscription</Badge>
            )}
          </CardTitle>
          {paymentStatus?.payment_sent && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchPaymentStatus(true)}
              disabled={refreshing}
              data-testid="refresh-payment-status-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        
        {/* SPLIT PAYMENTS VIEW */}
        {paymentType === "split" && (
          <div className="space-y-3">
            {/* Overall Progress Summary */}
            <div className="flex items-center justify-between text-sm mb-4 p-2 bg-sand/30 rounded-lg">
              <span className="text-muted-foreground">Progress</span>
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {payments.filter(p => p.status === "paid").length} of {splitPayments.length} paid
                </span>
                {payments.some(p => p.status === "pending") && (
                  <Badge className="bg-amber-500 text-xs">Invoice Sent</Badge>
                )}
              </div>
            </div>
            
            {splitPayments.map((payment, idx) => {
              const paymentRecord = payments.find(p => p.index === idx);
              const isPaid = paymentRecord?.status === "paid";
              const isPending = paymentRecord?.status === "pending";
              const isSent = isPaid || isPending;
              const isNext = !isSent && idx === currentPaymentIndex;
              const canSend = isNext && (payment.send_timing === "manual" || idx === 0);
              const isUpcoming = !isSent && !isNext;
              
              return (
                <div 
                  key={idx} 
                  data-testid={`split-payment-${idx}`}
                  className={`p-3 rounded-lg border ${
                    isPaid ? "bg-green-50 border-green-200" :
                    isPending ? "bg-amber-50 border-amber-200" :
                    isNext ? "bg-forest/5 border-forest" :
                    "bg-sand/30 border-stone/50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        isPaid ? "bg-green-600 text-white" :
                        isPending ? "bg-amber-500 text-white" :
                        isNext ? "bg-forest text-white" :
                        "bg-stone/30 text-muted-foreground"
                      }`}>
                        {isPaid ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{payment.due_description || `Payment ${idx + 1}`}</p>
                        <p className="text-xs text-muted-foreground">
                          ${parseFloat(payment.amount || 0).toLocaleString()}
                          {idx > 0 && payment.send_timing !== "manual" && (
                            <span className="ml-1">
                              • {payment.send_timing?.replace("after_", "")} days after previous
                            </span>
                          )}
                          {idx > 0 && payment.send_timing === "manual" && (
                            <span className="ml-1">• Manual send</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isPaid && (
                        <Badge className="bg-green-600" data-testid={`payment-${idx}-paid`}>Paid</Badge>
                      )}
                      {isPending && (
                        <Badge className="bg-amber-500" data-testid={`payment-${idx}-sent`}>Sent</Badge>
                      )}
                      {isUpcoming && (
                        <Badge variant="outline" className="text-muted-foreground" data-testid={`payment-${idx}-upcoming`}>Upcoming</Badge>
                      )}
                      {canSend && stripeConnected && (
                        <Button
                          size="sm"
                          onClick={() => sendPaymentRequest(idx)}
                          disabled={sendingPayment}
                          className="bg-forest hover:bg-forest/90"
                          data-testid={`send-payment-${idx}-btn`}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Send
                        </Button>
                      )}
                      {isNext && !stripeConnected && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">Next</Badge>
                      )}
                    </div>
                  </div>
                  {paymentRecord?.sent_at && (
                    <p className="text-xs text-muted-foreground mt-2 pl-9">
                      Sent {formatDate(paymentRecord.sent_at)}
                      {paymentRecord.paid_at && ` • Paid ${formatDate(paymentRecord.paid_at)}`}
                    </p>
                  )}
                  {/* Show checkout/invoice URL if pending */}
                  {isPending && paymentStatus?.checkout_url && idx === 0 && (
                    <div className="mt-2 pl-9">
                      <a 
                        href={paymentStatus.checkout_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-forest hover:underline flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View Invoice
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
            
            {/* Total */}
            <div className="pt-2 border-t border-stone/50 flex justify-between text-sm">
              <span className="text-muted-foreground">Total</span>
              <span className="font-semibold">${calculateTotal().toLocaleString()}</span>
            </div>
          </div>
        )}

        {/* ONE-TIME, SUBSCRIPTION, HYBRID VIEW */}
        {paymentType !== "split" && (
          <>
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge variant={
                overallStatus === "paid" ? "default" :
                overallStatus === "pending" ? "secondary" :
                overallStatus === "expired" ? "destructive" :
                "outline"
              } className={
                overallStatus === "paid" ? "bg-green-600" :
                overallStatus === "pending" ? "bg-amber-500" :
                ""
              }>
                {overallStatus === "paid" ? "Paid" :
                 overallStatus === "pending" ? "Awaiting Payment" :
                 overallStatus === "active" ? "Subscription Active" :
                 overallStatus === "expired" ? "Expired" :
                 "Not Sent"}
              </Badge>
            </div>

            {/* Amount */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {paymentType === "subscription" ? "Recurring" : 
                 paymentType === "hybrid" ? "Setup + Recurring" : "Amount"}
              </span>
              <span className="font-semibold">${calculateTotal().toLocaleString()}</span>
            </div>

            {/* Payment Not Sent - Show Send Button */}
            {!paymentStatus?.payment_sent && (
              <div className="space-y-3 pt-2">
                {!stripeConnected ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Stripe Not Connected</p>
                        <p className="text-xs text-amber-700 mt-1">
                          Connect your Stripe account in Settings → Integrations to send payment requests.
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={() => sendPaymentRequest()}
                    disabled={sendingPayment}
                    className="w-full bg-forest hover:bg-forest/90"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {sendingPayment ? "Sending..." : "Send Payment Request"}
                  </Button>
                )}
              </div>
            )}

            {/* Payment Sent - Show Tracking */}
            {paymentStatus?.payment_sent && (
              <div className="space-y-4">
                {/* Progress Tracker */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className={`flex flex-col items-center ${['pending', 'paid', 'active'].includes(overallStatus) ? 'text-forest' : ''}`}>
                    <div className={`w-3 h-3 rounded-full ${['pending', 'paid', 'active'].includes(overallStatus) ? 'bg-forest' : 'bg-slate-200'}`} />
                    <span className="mt-1">Sent</span>
                  </div>
                  <div className={`flex-1 h-0.5 mx-2 ${['paid', 'active'].includes(overallStatus) ? 'bg-forest' : 'bg-slate-200'}`} />
                  <div className={`flex flex-col items-center ${['paid', 'active'].includes(overallStatus) ? 'text-forest' : ''}`}>
                    <div className={`w-3 h-3 rounded-full ${['paid', 'active'].includes(overallStatus) ? 'bg-forest' : 'bg-slate-200'}`} />
                    <span className="mt-1">{paymentType === "subscription" ? "Active" : "Paid"}</span>
                  </div>
                </div>

                {/* Details */}
                <div className="bg-sand/50 rounded-lg p-3 space-y-2 text-sm">
                  {paymentStatus?.sent_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sent</span>
                      <span>{formatDate(paymentStatus.sent_at)}</span>
                    </div>
                  )}
                  {paymentStatus?.completed_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Completed</span>
                      <span className="text-green-600 font-medium">{formatDate(paymentStatus.completed_at)}</span>
                    </div>
                  )}
                </div>

                {/* View Payment Link */}
                {overallStatus === "pending" && paymentStatus?.checkout_url && (
                  <Button
                    variant="outline"
                    className="w-full"
                    asChild
                  >
                    <a 
                      href={paymentStatus.checkout_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      View Payment Page
                    </a>
                  </Button>
                )}

                {['paid', 'active'].includes(overallStatus) && (
                  <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 rounded-lg p-3">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">
                      {paymentType === "subscription" ? "Subscription Active" : "Payment Complete"}
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentTracker;
